import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { AuthService } from '@infra/auth/auth.service';
import { CondutorModel, GetCondutorModel } from '@data/condutor/models';
import { MunicipioRepository } from '@data/municipio/municipio.repository';
import { CondutorRepository } from '@data/condutor/condutor.repository';
import { Campo, FormComponent } from '@shared/components/form/form.component';
import { ContratoRepository } from '@data/contrato/contrato.repository';
import { FrotaContratadaModel } from '@data/contrato/models';
import { modal } from '@shared/alerts/modal/modal.alert';
import { cpfValidator } from '@shared/validators/cpf.validator';
import { cpfFormat } from '@shared/formatters';

@Component({
  selector: 'app-vincular-condutor',
  templateUrl: './vincular-condutor.component.html',
  styleUrls: ['./vincular-condutor.component.scss']
})
export class VincularCondutorComponent {

  @ViewChild('formVeiculo') formVeiculo: FormComponent | undefined;
  @ViewChild('formListagemCondutores') formListagemCondutores: FormComponent | undefined;
  @ViewChild('formCondutor') formCondutor: FormComponent | undefined;
  @ViewChild('formCNH') formCNH: FormComponent | undefined;

  protected loading: boolean = false;

  protected camposDadosVeiculo: Campo[] = [
    {
      name: 'modelo',
      label: 'Modelo',
      type: 'text',
      width: 'calc(100% / 3)',
      disabled: true,
    },
    {
      name: 'marca',
      label: 'Marca',
      type: 'text',
      width: 'calc(100% / 3)',
      disabled: true,
    },
    {
      name: 'placa',
      label: 'Placa',
      type: 'text',
      width: 'calc(100% / 3)',
      disabled: true,
    },
    {
      name: 'chassi',
      label: 'Chassi',
      type: 'text',
      width: '50%',
      disabled: true,
    },
    {
      name: 'renavam',
      label: 'Renavam',
      type: 'text',
      width: '50%',
      disabled: true,
    },
  ];

  protected camposListagemCondutores: Campo[] = [
    {
      name: 'pessoa.cpf',
      label: 'CPF do Condutor',
      type: 'text',
      width: 'calc(100% - 66px)',
      icon: 'search',
      mask: '000.000.000-00',
      onChange: this.buscarCondutores.bind(this)
    },
    {
      name: 'cadastar',
      label: '',
      type: 'button',
      width: '66px',
      icon: 'add',
      disabled: !this.authService.hasPermission('condutor', 'INSERIR'),
      buttonOptions: {
        tooltip: 'Cadastrar Condutor',
        onClick: () => {
            const w = window.open('/condutores', '_blank')

            if (w) {
              w.addEventListener('load', () => {
                w.postMessage({ message: 'INSERIR_CONDUTOR' }, location.origin);
              });
            }
          }
        }
    },
    {
      name: 'condutores',
      label: 'Condutores',
      type: 'table',
      width: '100%',
      tableOptions: {
        onSelect: this.selecionarCondutor.bind(this),
        colunas: [
          {
            name: 'nomeCondutor',
            label: 'Nome Condutor',
          },
          {
            name: 'cpf',
            label: 'CPF Condutor',
            format: cpfFormat
          },
          {
            name: 'rg',
            label: 'RG Condutor',
          },
        ]
      }
    },
    {
      name: 'paginator',
      label: 'Condutores',
      type: 'paginator',
      width: '100%',
      paginatorOptions: {
        onPaginate: (event) => {
          if(this.formListagemCondutores) {
            this.buscarCondutores(this.formListagemCondutores.get('pessoa.cpf'), event);
          }
        },
      }
    }
  ];

  protected camposDadosPessoa: Campo[] = [
    {
      name: 'pessoa.id',
      label: 'Código da Pessoa',
      type: 'text',
      width: '0%',
      hide: true,
      validators: [],
    },
    {
      name: 'pessoa.cpf',
      label: 'CPF',
      type: 'text',
      mask: '000.000.000-00',
      width: '50%',
      disabled: true,
      validators: [Validators.required, cpfValidator],
    },
    {
      name: 'pessoa.razaoSocial',
      label: 'Nome',
      type: 'text',
      width: '50%',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'pessoa.dataNascimento',
      label: 'Data de Nascimento',
      type: 'date',
      dateOptions: { 
        icon: 'suffix',
        maxDate: moment().subtract(18, 'years'),
      },
      width: '100%',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'pessoa.rg',
      label: 'RG',
      type: 'text',
      mask: '0*',
      width: 'calc(100% / 3)',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'pessoa.orgaoEmissorRg',
      label: 'Órgão Emissor RG',
      type: 'text',
      width: 'calc(100% / 3)',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
  ];

  protected camposDadosCNH: Campo[] = [
    {
      name: 'id',
      label: 'Código do Condutor',
      type: 'text',
      width: '0%',
      hide: true,
      validators: [],
    },
    {
      name: 'numeroCNH',
      label: 'Nº CNH',
      type: 'text',
      mask: '0*',
      width: 'calc(100% / 3)',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'registroCNH',
      label: 'Nº Registro CNH',
      type: 'text',
      mask: '0*',
      width: 'calc(100% / 3)',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'numeroSegurancaCNH',
      label: 'Nº Segurança CNH',
      type: 'text',
      mask: '0*',
      width: 'calc(100% / 3)',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'categoria',
      label: 'Categoria',
      type: 'select',
      selectOptions: {
        items: [
          { value: 'A', label: 'A' },
          { value: 'B', label: 'B' },
          { value: 'C', label: 'C' },
          { value: 'D', label: 'D' },
          { value: 'E', label: 'E' },
          { value: 'AB', label: 'AB' },
          { value: 'AC', label: 'AC' },
          { value: 'AD', label: 'AD' },
          { value: 'AE', label: 'AE' },
        ],
      },
      width: '30%',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'orgaoCNH',
      label: 'Órgão Emissor CNH',
      type: 'text',
      width: '30%',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'dataValidadeCNH',
      label: 'Validade',
      type: 'date',
      dateOptions: { 
        icon: 'suffix',
        minDate: moment(),
        maxDate: moment().add(10, 'years'),
      },
      width: '40%',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'dataPrimeiraHabilitacao',
      label: 'Data Primeira Habilitação',
      type: 'date',
      dateOptions: { 
        icon: 'suffix',
        maxDate: moment(),
      },
      width: '40%',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
    {
      name: 'municipioEmissorCNHId',
      label: 'Município',
      type: 'search',
      searchOptions: {
        key: 'descricao',
        primaryKey: 'id',
        format: (value: string) => value + '%',
        filter: this.municipioRepository.filter.bind(this.municipioRepository),
      },
      width: '60%',
      disabled: !this.authService.hasPermission('condutor', 'EDITAR'),
      validators: [Validators.required],
    },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FrotaContratadaModel,
    private dialogRef: MatDialogRef<any>,
    private condutorRepository: CondutorRepository,
    private contratoRepository: ContratoRepository,
    private municipioRepository: MunicipioRepository,
    protected authService: AuthService,
    private changeDetector: ChangeDetectorRef,
  ) {
    window.addEventListener('message', (event) => {
      if (event.data.message === 'CONDUTOR_INSERIDO') {
        const condutor = JSON.parse(event.data.data);

        if(this.formListagemCondutores && condutor) {
          this.formListagemCondutores.patchValue({ 'pessoa.cpf': condutor.pessoa.cpf });
        }
      }
    });
  }

  protected get isFormValid() {
    return (
      this.formCondutor?.isValid &&
      this.formCNH?.isValid
    );
  }

  /** Para evitar erros em ambiente de desenvolvimento */
  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }

  ngAfterViewInit() {
    if(this.formVeiculo && this.data) {
      this.formVeiculo.load(this.data);
      this.buscarCondutores('');
    }
  }

  buscarCondutores(cpf: string, pagination: PageEvent = { pageIndex: 0, pageSize: 5, length: 0 }) {
    this.loading = true;

    const params = {
      search: `pessoa.cpf=%${cpf || ''}%`,
      page: pagination.pageIndex + 1,
      offset: pagination.pageSize * pagination.pageIndex,
      limit: pagination.pageSize,
    };

    this.condutorRepository.getRequest<{ data: { results: GetCondutorModel[], count: number }}>(params).subscribe((result) => {
      if(result.data.results && this.formListagemCondutores) {
        this.formListagemCondutores.load({ 
          condutores: result.data.results, 
          paginator: {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            length: result.data.count,
          }
        });
      }
    }).add(() => { this.loading = false });
  }

  selecionarCondutor(condutor: GetCondutorModel) {
    this.loading = true;
    
    this.condutorRepository.getByIdRequest<{ data: CondutorModel }>(condutor.codigoCondutor).subscribe((result) => {
      if (result.data && this.formCondutor && this.formCNH) {
        this.formCondutor.reset();
        this.formCNH.reset();
        this.formCondutor.load(result.data);
        this.formCNH.load(result.data);
      }
    }).add(() => { this.loading = false });
  }

  onSubmit() {
    if(!this.formCNH || !this.formCondutor) return;

    this.loading = true;

    const condutor = { ...this.formCNH.values, ...this.formCondutor.values };

    this.condutorRepository.putRequest(condutor.id, condutor).subscribe({
      next: () => {
        this.contratoRepository.vincularCondutor(this.data.codigoContrato, condutor.id).subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: (error) => {
            modal.fire({
              icon: error.icon,
              title: error.title,
              html: error.message,
            });
    
            this.loading = false
          },
        }).add(() => { this.loading = false });
      },
      error: (error) => {
        modal.fire({
          icon: error.icon,
          title: error.title,
          html: error.message,
        });

        this.loading = false
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
